import axios from 'axios';

const attachAuthInterceptors = (instance) => {
  instance.interceptors.request.use((config) => {
    const token = localStorage.getItem('skit_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  instance.interceptors.response.use(
    (res) => res,
    async (err) => {
      const url = err.config?.url || '';
      // /auth/login's own 401 is just "wrong password" — let the form show it.
      // /auth/me's 401 just means "no session yet" — AuthContext already treats
      // that as logged-out, no need to hard-redirect (doing so here caused a
      // reload -> /auth/me -> 401 -> reload infinite loop on first visit).
      const isSessionCheck = url.includes('/auth/login') || url.includes('/auth/me');
      if (err.response?.status === 401 && !isSessionCheck) {
        localStorage.removeItem('skit_token');
        localStorage.removeItem('skit_user');
        window.location.href = '/login';
      }
      // Every file-download call (templates, slips) sets responseType:'blob'
      // so a successful response comes back as binary — but axios honors
      // that same responseType on an ERROR response too, even though the
      // server actually sent plain JSON for the error case. That left
      // err.response.data as an opaque Blob, so every caller's
      // `err.response?.data?.message` read silently came back undefined and
      // fell through to the same generic "Failed to..." string regardless
      // of the real cause (permission error, server crash, whatever) —
      // exactly the bug that hid why a template download was failing.
      // Unwrapping it once here, centrally, means every existing and future
      // `err.response?.data?.message` read just works, for every blob
      // download in the app, without each call site handling it itself.
      if (err.response?.data instanceof Blob && err.config?.responseType === 'blob') {
        try {
          const text = await err.response.data.text();
          err.response.data = JSON.parse(text);
        } catch {
          // Not JSON (e.g. an HTML error page from a proxy in front of the
          // API) — leave it as the Blob; the caller's fallback message
          // still applies in that case.
        }
      }
      return Promise.reject(err);
    }
  );
  return instance;
};

const api = attachAuthInterceptors(axios.create({ baseURL: process.env.REACT_APP_API_URL }));

// Workers are MaterialWatch-owned data, but Portal's User Management UI also
// manages "Contract Workers" through the same feature. On the Portal build
// (REACT_APP_MATERIALWATCH_URL set), Portal's own backend has no /workers
// route at all — this instance calls MaterialWatch's backend directly
// instead. On the MaterialWatch build itself, it's the same same-origin call
// as `api` (the var is unset there), so nothing changes for that build.
//
// withCredentials is required here: a user who logged in on one origin and
// navigated to the other has no localStorage token there (localStorage is
// per-origin) — only the shared cross-subdomain cookie proves who they are.
// Without withCredentials, axios silently drops that cookie on cross-origin
// requests even though the browser already holds it, and the request 401s.
const materialwatchApi = attachAuthInterceptors(
  axios.create({
    baseURL: process.env.REACT_APP_MATERIALWATCH_URL
      ? `${process.env.REACT_APP_MATERIALWATCH_URL}/api`
      : process.env.REACT_APP_API_URL,
    withCredentials: true,
  })
);

// Users are Portal-owned data. On the MaterialWatch build
// (REACT_APP_PORTAL_URL set), MaterialWatch's own backend has no /users
// route at all — this instance calls Portal's backend directly instead. On
// the Portal build itself, it's the same same-origin call as `api` (the var
// is unset there). See materialwatchApi above for why withCredentials is
// required.
const portalApi = attachAuthInterceptors(
  axios.create({
    baseURL: process.env.REACT_APP_PORTAL_URL
      ? `${process.env.REACT_APP_PORTAL_URL}/api`
      : process.env.REACT_APP_API_URL,
    withCredentials: true,
  })
);

// Task Manager is its own separate app/backend (see task-manager repo) — its
// backend verifies the same shared skit_token JWT Portal issues, so calling
// it directly cross-origin works the same way materialwatchApi does above.
// Used for the notification-summary poll that drives Task Manager's badge
// on Portal's own module launcher tile.
const taskManagerApi = attachAuthInterceptors(
  axios.create({
    baseURL: process.env.REACT_APP_TASK_MANAGER_URL
      ? `${process.env.REACT_APP_TASK_MANAGER_URL}/api`
      : process.env.REACT_APP_API_URL,
    withCredentials: true,
  })
);

export { materialwatchApi, portalApi, taskManagerApi };
export default api;
